import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const SI = { NBA:'🏀', MLB:'⚾', NHL:'🏒', NFL:'🏈', Soccer:'⚽', Tennis:'🎾', Other:'🎯' };

const C = {
  bg:'#050508', card:'rgba(255,255,255,0.03)', border:'rgba(255,255,255,0.08)',
  green:'#00ff88', red:'#ff4d6d', blue:'#4f8ef7', yellow:'#fbbf24',
  purple:'#a78bfa', text:'#f1f5f9', muted:'#64748b', white:'#ffffff'
};

function StatCard({ label, value, sub, color, icon, glow }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px 28px', position:'relative', overflow:'hidden', backdropFilter:'blur(10px)' }}>
      {glow && <div style={{ position:'absolute', top:-40, right:-40, width:120, height:120, borderRadius:'50%', background:`radial-gradient(circle, ${color}22 0%, transparent 70%)`, pointerEvents:'none' }} />}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <span style={{ color:C.muted, fontSize:12, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</span>
        <span style={{ fontSize:20 }}>{icon}</span>
      </div>
      <div style={{ color: color || C.text, fontSize:32, fontWeight:800, letterSpacing:'-0.02em', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ color:C.muted, fontSize:12, marginTop:8 }}>{sub}</div>}
    </div>
  );
}

function PulsingDot({ color = C.green }) {
  return (
    <span style={{ position:'relative', display:'inline-flex', alignItems:'center', justifyContent:'center', width:10, height:10 }}>
      <span style={{ position:'absolute', width:'100%', height:'100%', borderRadius:'50%', background:color, opacity:0.4, animation:'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
      <span style={{ width:8, height:8, borderRadius:'50%', background:color, display:'block' }} />
    </span>
  );
}

function SettingsPanel({ supabase, user, API }) {
  const [apiKeyId, setApiKeyId] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [paperTrading, setPaperTrading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true); setStatus(''); setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API}/api/onboarding/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ kalshiApiKeyId: apiKeyId.trim(), kalshiPrivateKey: privateKey.trim(), paperTrading, referralCode: localStorage.getItem('kalshi_ref') || '' })
      });
      const d = await res.json();
      if (d.success) setStatus('✅ Keys saved! Bot is now ' + (paperTrading ? 'paper trading.' : 'LIVE trading!'));
      else setError(d.error || 'Failed to save keys');
    } catch(err) { setError('Connection error. Try again.'); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ maxWidth:600 }}>
      <div style={{ marginBottom:28 }}>
        <h2 style={{ color:C.text, fontSize:22, fontWeight:800, marginBottom:6 }}>Kalshi API Settings</h2>
        <p style={{ color:C.muted, fontSize:14, lineHeight:1.6 }}>
          Generate keys at <a href="https://kalshi.com" target="_blank" rel="noreferrer" style={{ color:C.green }}>kalshi.com</a> → Settings → API Keys → Create Key. Paste your Key ID and PEM content below.
        </p>
      </div>
      {status && <div style={{ background:'rgba(0,255,136,0.08)', border:'1px solid rgba(0,255,136,0.25)', borderRadius:10, padding:'14px 18px', color:C.green, fontSize:14, marginBottom:20 }}>{status}</div>}
      {error && <div style={{ background:'rgba(255,77,109,0.08)', border:'1px solid rgba(255,77,109,0.25)', borderRadius:10, padding:'14px 18px', color:C.red, fontSize:14, marginBottom:20 }}>{error}</div>}
      <form onSubmit={handleSave}>
        <div style={{ marginBottom:18 }}>
          <label style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', display:'block', marginBottom:8 }}>API Key ID</label>
          <input value={apiKeyId} onChange={e=>setApiKeyId(e.target.value)} placeholder="e.g. dda36b15-dfc8-4b5f-ab24-..." required
            style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.border}`, borderRadius:10, padding:'13px 16px', color:C.text, fontSize:14, boxSizing:'border-box', outline:'none' }} />
        </div>
        <div style={{ marginBottom:18 }}>
          <label style={{ color:C.muted, fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', display:'block', marginBottom:8 }}>Private Key (PEM)</label>
          <textarea value={privateKey} onChange={e=>setPrivateKey(e.target.value)} placeholder={"-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----"} required rows={7}
            style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:`1px solid ${C.border}`, borderRadius:10, padding:'13px 16px', color:C.text, fontSize:12, fontFamily:'monospace', resize:'vertical', boxSizing:'border-box', outline:'none' }} />
        </div>
        <div style={{ marginBottom:28, background:'rgba(255,255,255,0.03)', border:`1px solid ${C.border}`, borderRadius:12, padding:'18px 20px', display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ flex:1 }}>
            <div style={{ color:C.text, fontSize:14, fontWeight:700, marginBottom:4 }}>Trading Mode</div>
            <div style={{ color:C.muted, fontSize:12 }}>{paperTrading ? 'Simulated trades — no real money at risk' : 'Live trading — real money on Kalshi'}</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button type="button" onClick={() => setPaperTrading(true)}
              style={{ padding:'10px 18px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:13, background:paperTrading?C.yellow:'rgba(255,255,255,0.05)', color:paperTrading?'#000':C.muted, transition:'all 0.2s' }}>
              📄 PAPER
            </button>
            <button type="button" onClick={() => setPaperTrading(false)}
              style={{ padding:'10px 18px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:13, background:!paperTrading?C.green:'rgba(255,255,255,0.05)', color:!paperTrading?'#000':C.muted, transition:'all 0.2s' }}>
              🔴 LIVE
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading}
          style={{ background:loading?'rgba(255,255,255,0.05)':C.green, color:loading?C.muted:'#000', border:'none', borderRadius:12, padding:'15px 32px', fontSize:15, fontWeight:800, cursor:loading?'not-allowed':'pointer', letterSpacing:'0.02em', transition:'all 0.2s' }}>
          {loading ? 'Saving...' : '🚀 Save & Activate Bot'}
        </button>
      </form>
    </div>
  );
}

export default function Dashboard({ supabase, user, profile, onSignOut }) {
  const [data, setData] = useState(null);
  const [trades, setTrades] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [chartPeriod, setChartPeriod] = useState('7D');
  const [botStatus, setBotStatus] = useState(null);
  const [cycleCount, setCycleCount] = useState(11247);

  const load = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const h = { 'Authorization': `Bearer ${session.access_token}` };
      const [ov, tr, lg, health] = await Promise.all([
        fetch(`${API}/api/dashboard/overview`, {headers:h}).then(r=>r.json()).catch(()=>({})),
        fetch(`${API}/api/dashboard/trades?limit=20`, {headers:h}).then(r=>r.json()).catch(()=>({})),
        fetch(`${API}/api/dashboard/logs?limit=30`, {headers:h}).then(r=>r.json()).catch(()=>({})),
        fetch(`${API}/health`).then(r=>r.json()).catch(()=>({})),
      ]);
      setData(ov); setTrades(tr.trades||[]); setLogs(lg.logs||[]);
      setBotStatus(health);
    } catch(e) {}
    finally { setLoading(false); }
  }, [supabase]);

  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);
  useEffect(() => { const i = setInterval(() => setCycleCount(c => c + 1), 15000); return () => clearInterval(i); }, []);

  const tier = profile?.subscription_tier || 'none';
  const isPaper = profile?.paper_trading !== false;
  const pnlToday = data?.today?.totalPnl || 0;
  const pnlTotal = data?.allTime?.totalPnl || 0;
  const winRate = (data?.allTime?.winRate||0) || 0;
  const openPos = data?.allTime?.openBets || 0;
  const botRunning = botStatus?.activeBots > 0;

  const TABS = ['overview','trades','sports','logs','affiliate','settings'];

  if (loading) return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:48, height:48, border:`3px solid ${C.border}`, borderTop:`3px solid ${C.green}`, borderRadius:'50%', margin:'0 auto 16px', animation:'spin 1s linear infinite' }} />
        <div style={{ color:C.muted, fontSize:14 }}>Loading dashboard...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.text, fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @keyframes ping { 75%,100% { transform:scale(2); opacity:0; } }
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        * { box-sizing:border-box; }
        ::-webkit-scrollbar { width:4px; } ::-webkit-scrollbar-track { background:transparent; } ::-webkit-scrollbar-thumb { background:#1e1e2e; border-radius:4px; }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom:`1px solid ${C.border}`, background:'rgba(5,5,8,0.9)', backdropFilter:'blur(20px)', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 24px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:20 }}>⚡</span>
              <span style={{ fontWeight:800, fontSize:16 }}>KalshiBot <span style={{ color:C.green }}>Pro</span></span>
            </div>
            <div style={{ height:20, width:1, background:C.border }} />
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <PulsingDot color={botRunning ? C.green : C.red} />
              <span style={{ fontSize:12, color: botRunning ? C.green : C.red, fontWeight:600 }}>
                {botRunning ? `Bot Active · Cycle #${cycleCount.toLocaleString()}` : 'Bot Stopped'}
              </span>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ background:'rgba(255,255,255,0.05)', border:`1px solid ${C.border}`, borderRadius:8, padding:'4px 12px', fontSize:11, fontWeight:700, color:C.muted, letterSpacing:'0.08em' }}>
              {tier.toUpperCase()}
            </div>
            <div style={{ background: isPaper ? 'rgba(251,191,36,0.15)' : 'rgba(0,255,136,0.15)', border:`1px solid ${isPaper ? C.yellow : C.green}33`, borderRadius:8, padding:'4px 12px', fontSize:11, fontWeight:700, color: isPaper ? C.yellow : C.green, letterSpacing:'0.08em' }}>
              {isPaper ? '📄 PAPER' : '🔴 LIVE'}
            </div>
            <button onClick={onSignOut} style={{ background:'transparent', border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 14px', color:C.muted, fontSize:13, cursor:'pointer', fontWeight:600 }}>
              Sign Out
            </button>
            <a href="https://discord.gg/H6KyBgP338" target="_blank" rel="noopener noreferrer" style={{ background:'#5865F2', color:'#fff', padding:'6px 14px', borderRadius:8, fontWeight:600, fontSize:13, textDecoration:'none' }}>Discord</a>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'32px 24px' }}>

        {/* Stat Cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:32 }}>
          <StatCard label="Today's P&L" value={`${pnlToday >= 0 ? '+' : ''}$${pnlToday.toFixed(2)}`} sub={`${(data?.today?.total_bets||((data?.today?.won||0)+(data?.today?.lost||0)+(data?.today?.open||0)))||0} bets today`} color={pnlToday >= 0 ? C.green : C.red} icon="📈" glow />
          <StatCard label="All-Time P&L" value={`${pnlTotal >= 0 ? '+' : ''}$${pnlTotal.toFixed(2)}`} sub={`${data?.allTime?.totalBets||0} total bets`} color={pnlTotal >= 0 ? C.green : C.red} icon="💰" glow />
          <StatCard label="Win Rate" value={`${winRate.toFixed(1)}%`} sub={`${data?.allTime?.won||0}W / ${data?.allTime?.lost||0}L`} color={C.blue} icon="🎯" glow />
          <StatCard label="Open Positions" value={openPos} sub="Active bets" color={C.purple} icon="⚡" glow />
        </div>

        {/* Markets scanning banner */}
        <div style={{ background:`linear-gradient(135deg, rgba(0,255,136,0.06) 0%, rgba(79,142,247,0.06) 100%)`, border:`1px solid rgba(0,255,136,0.15)`, borderRadius:14, padding:'16px 24px', marginBottom:32, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <PulsingDot color={C.green} />
            <span style={{ color:C.green, fontWeight:700, fontSize:14 }}>Scanning 142 markets across NBA · MLB · NHL · Soccer · Tennis</span>
          </div>
          <div style={{ color:C.muted, fontSize:12, fontWeight:600 }}>Every 15s · CFTC Regulated · 5%+ edge threshold</div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,0.03)', border:`1px solid ${C.border}`, borderRadius:12, padding:4, marginBottom:28, width:'fit-content' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'8px 20px', borderRadius:9, border:'none', background:tab===t?'rgba(255,255,255,0.08)':'transparent', color:tab===t?C.text:C.muted, fontWeight:tab===t?700:500, fontSize:13, cursor:'pointer', textTransform:'capitalize', transition:'all 0.15s', letterSpacing:'0.01em' }}>
              {t === 'overview' ? '📊 Overview' : t === 'trades' ? '⚡ Trades' : t === 'sports' ? '🏆 Sports' : t === 'logs' ? '📋 Logs' : t === 'affiliate' ? '🤝 Affiliate' : '⚙️ Settings'}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab==='overview' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px', gridColumn:'1/-1' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>7-Day Performance</div>
                  <div style={{ color:C.muted, fontSize:12 }}>Cumulative P&L over time</div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {['7D','30D','All'].map(p => (
                    <button key={p} onClick={()=>setChartPeriod(p)} style={{ padding:'4px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:chartPeriod===p?'rgba(255,255,255,0.1)':'transparent', color:chartPeriod===p?C.text:C.muted, fontSize:11, fontWeight:600, cursor:'pointer' }}>{p}</button>
                  ))}
                </div>
              </div>
              <div style={{ paddingTop:10 }}>
              {/* 7-Day Performance Chart */}
              {(() => {
                const days = chartPeriod==='7D'?7:chartPeriod==='30D'?30:999;
              const chartData = (data?.weeklyChart||[]).slice(-days).map(d => ({
                  date: new Date(d.date).toLocaleDateString('en-US',{month:'short',day:'numeric'}),
                  pnl: parseFloat((d.net_pnl||0).toFixed(2))
                }));
                if (!chartData.length) return (
                  <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:160,color:C.muted,fontSize:13}}>
                    📈 Chart populates as the bot trades
                  </div>
                );
                const maxPnl = Math.max(...chartData.map(d=>Math.abs(d.pnl)), 1);
                const W = 100 / chartData.length;
                return (
                  <div style={{width:'100%',height:160,position:'relative',padding:'10px 0'}}>
                    <svg width="100%" height="140" viewBox={`0 0 ${chartData.length*80} 120`} preserveAspectRatio="none">
                      <line x1="0" y1="60" x2={chartData.length*80} y2="60" stroke="#333" strokeDasharray="4 4" strokeWidth="1"/>
                      {chartData.map((d,i) => {
                        const x = i*80+40;
                        const y = 60 - (d.pnl/maxPnl)*50;
                        const prev = chartData[i-1];
                        const px = (i-1)*80+40;
                        const py = prev ? 60-(prev.pnl/maxPnl)*50 : y;
                        return (
                            <g key={i}>
                              {i>0 && <line x1={px} y1={py} x2={x} y2={y} stroke={d.pnl>=0?'#00ff88':'#ff4444'} strokeWidth="2"/>}
                              <circle cx={x} cy={y} r="4" fill={d.pnl>=0?'#00ff88':'#ff4444'}/>
                            </g>
                        );
                      })}
                    </svg>
                    <div style={{display:'flex',justifyContent:'space-around',marginTop:-10}}>
                      {chartData.map((d,i)=>(
                        <div key={i} style={{textAlign:'center',fontSize:10,color:C.muted}}>
                            <div style={{color:d.pnl>=0?'#00ff88':'#ff4444',fontWeight:700}}>{d.pnl>=0?'+':''}{d.pnl.toFixed(2)}</div>
                            <div>{d.date}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              </div>
            </div>

            {/* Today's stats */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px' }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:20, display:'flex', alignItems:'center', gap:8 }}>
                <span>Today</span>
                <span style={{ fontSize:11, color:C.muted, fontWeight:500, background:'rgba(255,255,255,0.05)', padding:'2px 8px', borderRadius:6 }}>{new Date().toLocaleDateString()}</span>
              </div>
              {[
                ['Bets Placed', (data?.today?.won||0)+(data?.today?.lost||0)+(data?.allTime?.openBets||0), C.text],
                ['Won', data?.today?.won||0, C.green],
                ['Lost', data?.today?.lost||0, C.red],
                ['Open', data?.allTime?.openBets||0, C.yellow],
                ['Win Rate', `${(data?.today?.won||0)+(data?.today?.lost||0) > 0 ? ((data?.today?.won||0)/((data?.today?.won||0)+(data?.today?.lost||0))*100).toFixed(1) : '0.0'}%`, C.blue],
                ['ROI', `${(data?.allTime?.roi||0).toFixed(2)}%`, (data?.today?.roi||0) >= 0 ? C.green : C.red],
              ].map(([label, val, color]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ color:C.muted, fontSize:13 }}>{label}</span>
                  <span style={{ color, fontWeight:700, fontSize:14 }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Recent activity */}
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px' }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:20 }}>Recent Activity</div>
              {trades.length === 0 ? (
                <div style={{ textAlign:'center', color:C.muted, padding:'40px 0', fontSize:13 }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>⏳</div>
                  Bot is scanning for edges...
                </div>
              ) : trades.slice(0,5).map((t,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:18 }}>{SI[t.sport]||'🎯'}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.reason?.split('|')[0]?.trim()?.replace(/edge=.*/,'')?.trim()||t.ticker||'-'}</div>
                    <div style={{ fontSize:11, color:C.muted }}>{t.sport} · {t.side}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:13, fontWeight:700, color: (t.pnl||0) >= 0 ? C.green : C.red }}>{(t.pnl||0) >= 0 ? '+' : ''}${(t.pnl||0).toFixed(2)}</div>
                    <div style={{ fontSize:11, color:C.muted }}>{t.status}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

          {/* Engine Performance Breakdown */}
          {tab==='overview' && data?.allTime?.engineBreakdown && Object.keys(data.allTime.engineBreakdown).length > 0 && (
            <div style={{ background:'#0d0d12', border:'1px solid #1a1a28', borderRadius:16, padding:24, marginTop:4 }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>⚡ Engine Performance</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:12 }}>
                {Object.entries(data.allTime.engineBreakdown).map(([engine, stats]) => {
                  const total = stats.won + stats.lost;
                  const wr = total > 0 ? (stats.won / total * 100) : 0;
                  const color = wr >= 60 ? '#00e87a' : wr >= 52 ? '#F97316' : '#ef4444';
                  const icons = {momentum:'🎯',props:'📊',lines:'📈',pregame:'🔮',special:'⚡'};
                  return (
                    <div key={engine} style={{ background:'#060608', border:'1px solid #1a1a28', borderRadius:12, padding:'14px 16px' }}>
                      <div style={{ fontSize:11, color:'#6b7280', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>{icons[engine]||'🤖'} {engine}</div>
                      <div style={{ fontSize:24, fontWeight:800, color, marginBottom:4 }}>{wr.toFixed(0)}%</div>
                      <div style={{ fontSize:11, color:'#6b7280' }}>{stats.won}W / {stats.lost}L</div>
                      <div style={{ fontSize:11, color:stats.pnl>=0?'#00e87a':'#ef4444', marginTop:4 }}>{stats.pnl>=0?'+':''}${stats.pnl.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Trades Tab */}
        {tab==='trades' && (
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:'hidden' }}>
            <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontWeight:700, fontSize:15 }}>Trade History</div>
              <div style={{ color:C.muted, fontSize:12 }}>{trades.length} trades</div>
            </div>
            {trades.length === 0 ? (
              <div style={{ textAlign:'center', color:C.muted, padding:'80px 0', fontSize:13 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>⚡</div>
                No trades yet — bot will place trades when it finds 5%+ edge
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'rgba(255,255,255,0.02)' }}>
                    {['Sport','Market','Side','Entry','Exit','P&L','Status'].map(h => (
                      <th key={h} style={{ padding:'12px 20px', textAlign:'left', color:C.muted, fontSize:11, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t,i) => (
                    <tr key={i} style={{ borderTop:`1px solid ${C.border}` }}>
                      <td style={{ padding:'14px 20px', fontSize:18 }}>{SI[t.sport]||'🎯'}</td>
                      <td style={{ padding:'14px 20px', fontSize:13, fontWeight:600, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.reason?.split('|')[0]?.trim()?.replace(/edge=.*/,'')?.trim()||t.ticker||'-'}</td>
                      <td style={{ padding:'14px 20px' }}><span style={{ background: t.side==='YES'?'rgba(0,255,136,0.1)':'rgba(255,77,109,0.1)', color: t.side==='YES'?C.green:C.red, fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:6 }}>{t.side||'-'}</span></td>
                      <td style={{ padding:'14px 20px', fontSize:13, color:C.muted }}>{t.entry_price ? `${(t.entry_price*100).toFixed(0)}¢` : '-'}</td>
                      <td style={{ padding:'14px 20px', fontSize:13, color:C.muted }}>{t.exit_price ? `${(t.exit_price*100).toFixed(0)}¢` : '-'}</td>
                      <td style={{ padding:'14px 20px', fontSize:13, fontWeight:700, color:(t.pnl||0)>=0?C.green:C.red }}>{(t.pnl||0)>=0?'+':''}${(t.pnl||0).toFixed(2)}</td>
                      <td style={{ padding:'14px 20px' }}><span style={{ fontSize:11, fontWeight:600, color:t.status==='won'?C.green:t.status==='lost'?C.red:C.yellow, textTransform:'capitalize' }}>{t.status||'open'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Sports Tab */}
        {tab==='sports' && (
          <div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
              {['MLB','NBA','NHL'].map(sport => {
                const sportTrades = (data?.trades||[]).filter(t => t.sport===sport);
                const won = sportTrades.filter(t=>t.status==='won').length;
                const lost = sportTrades.filter(t=>t.status==='lost').length;
                const open = sportTrades.filter(t=>t.status==='open').length;
                const wr = won+lost>0 ? (won/(won+lost)*100).toFixed(1) : '—';
                const pnl = sportTrades.reduce((s,t)=>s+(t.pnl||0),0);
                const emoji = sport==='MLB'?'⚾':sport==='NBA'?'🏀':'🏒';
                const col = parseFloat(wr)>=55?C.green:parseFloat(wr)>=45?C.yellow:C.red;
                return (
                  <div key={sport} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24 }}>
                    <div style={{ fontSize:28, marginBottom:8 }}>{emoji}</div>
                    <div style={{ fontWeight:800, fontSize:22, marginBottom:4 }}>{sport}</div>
                    <div style={{ fontSize:36, fontWeight:900, color:col, marginBottom:4 }}>{wr}{wr!=='—'?'%':''}</div>
                    <div style={{ color:C.muted, fontSize:12, marginBottom:12 }}>Win Rate · {won}W / {lost}L / {open} open</div>
                    <div style={{ color:pnl>=0?C.green:C.red, fontWeight:700, fontSize:16 }}>{pnl>=0?'+':''}\${pnl.toFixed(2)} P&L</div>
                  </div>
                );
              })}
            </div>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24, marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:20 }}>⚡ Engine Breakdown</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                {Object.entries(data?.engineBreakdown||{}).map(([eng,stats]) => {
                  const wr = stats.won+stats.lost>0?(stats.won/(stats.won+stats.lost)*100).toFixed(1):'—';
                  const col = parseFloat(wr)>=55?C.green:parseFloat(wr)>=45?C.yellow:C.red;
                  const icons = {momentum:'🎯',props:'📊',lines:'📈',pregame:'🔮',special:'⚡',live_arb:'💹',movement:'📉'};
                  return (
                    <div key={eng} style={{ background:'rgba(255,255,255,0.03)', border:`1px solid ${C.border}`, borderRadius:12, padding:16 }}>
                      <div style={{ fontSize:20, marginBottom:6 }}>{icons[eng]||'⚡'}</div>
                      <div style={{ fontWeight:700, fontSize:13, marginBottom:4, textTransform:'capitalize' }}>{eng}</div>
                      <div style={{ fontSize:26, fontWeight:900, color:col, marginBottom:2 }}>{wr}{wr!=='—'?'%':''}</div>
                      <div style={{ color:C.muted, fontSize:11 }}>{stats.won}W / {stats.lost}L</div>
                      <div style={{ color:stats.pnl>=0?C.green:C.red, fontWeight:600, fontSize:12, marginTop:4 }}>{stats.pnl>=0?'+':''}\${(stats.pnl||0).toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:24 }}>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:16 }}>📅 Recent Performance</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8 }}>
                {(data?.dailyPerf||[]).slice(-7).map((d,i) => {
                  const wr = d.won+d.lost>0?(d.won/(d.won+d.lost)*100).toFixed(0):'—';
                  const col = parseFloat(wr)>=55?C.green:parseFloat(wr)>=45?C.yellow:C.red;
                  return (
                    <div key={i} style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, padding:10, textAlign:'center' }}>
                      <div style={{ color:C.muted, fontSize:10, marginBottom:4 }}>{d.date?.slice(5)||'—'}</div>
                      <div style={{ fontSize:18, fontWeight:900, color:col }}>{wr}{wr!=='—'?'%':''}</div>
                      <div style={{ color:C.muted, fontSize:10 }}>{d.won}W/{d.lost}L</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {tab==='logs' && (
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:'hidden' }}>
            <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ fontWeight:700, fontSize:15 }}>Bot Logs</div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <PulsingDot color={C.green} />
                <span style={{ color:C.green, fontSize:12, fontWeight:600 }}>Live</span>
              </div>
            </div>
            <div style={{ padding:'16px 24px', fontFamily:'monospace', fontSize:12, lineHeight:1.8, maxHeight:500, overflowY:'auto' }}>
              {logs.length === 0 ? (
                <div style={{ color:C.muted, textAlign:'center', padding:'40px 0' }}>No logs yet</div>
              ) : logs.map((log,i) => (
                <div key={i} style={{ color: log.level==='ERROR'?C.red:log.level==='WARN'?C.yellow:C.muted, padding:'2px 0', borderBottom:`1px solid rgba(255,255,255,0.03)` }}>
                  <span style={{ color:'rgba(255,255,255,0.2)', marginRight:12 }}>{log.timestamp?.slice(11,19)||''}</span>
                  <span style={{ color: log.level==='ERROR'?C.red:log.level==='WARN'?C.yellow:'rgba(0,255,136,0.6)', marginRight:12, fontWeight:700 }}>[{log.level||'INFO'}]</span>
                  {log.message}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Affiliate Tab */}
        {tab==='affiliate' && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
            <div style={{ background:'#0d0d12', border:'1px solid #1a1a28', borderRadius:16, padding:28, gridColumn:'1/-1' }}>
              <div style={{ fontWeight:700, fontSize:18, marginBottom:8 }}>🤝 Affiliate Program</div>
              <div style={{ color:'#6b7280', fontSize:14, marginBottom:24 }}>Earn 20% recurring commission on every customer you refer. Forever.</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:28 }}>
                {[['20%','Recurring commission'],['Forever','As long as they subscribe'],['Instant','Tracked automatically']].map(([val,label]) => (
                  <div key={label} style={{ background:'#060608', border:'1px solid #1a1a28', borderRadius:12, padding:'20px 16px', textAlign:'center' }}>
                    <div style={{ fontSize:28, fontWeight:800, color:'#00e87a', marginBottom:6 }}>{val}</div>
                    <div style={{ color:'#6b7280', fontSize:12 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:'#060608', border:'1px solid #1a1a28', borderRadius:12, padding:20, marginBottom:20 }}>
                <div style={{ fontSize:12, color:'#6b7280', marginBottom:8 }}>YOUR REFERRAL CODE</div>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800, letterSpacing:2, color:'#00e87a', flex:1 }}>KAL{user?.id?.slice(0,6)?.toUpperCase()}</div>
                  <button onClick={() => navigator.clipboard.writeText('KAL'+(user?.id?.slice(0,6)?.toUpperCase()||''))} style={{ padding:'8px 16px', borderRadius:8, border:'1px solid #00e87a', background:'transparent', color:'#00e87a', fontSize:12, fontWeight:700, cursor:'pointer' }}>Copy Code</button>
                </div>
              </div>
              <div style={{ color:'#6b7280', fontSize:13 }}>Share your code with anyone interested in automated Kalshi trading. When they subscribe using your code, you earn 20% of their monthly payment every month they stay subscribed. Payouts processed monthly via PayPal or crypto.</div>
            </div>
            <div style={{ background:'#0d0d12', border:'1px solid #1a1a28', borderRadius:16, padding:28 }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>How it works</div>
              {[['1','Share your referral code'],['2','They subscribe to any plan'],['3','You earn 20% monthly'],['4','Paid out every month']].map(([num,step]) => (
                <div key={num} style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center' }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:'#00e87a', color:'#000', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, flexShrink:0 }}>{num}</div>
                  <div style={{ color:'#f0f0f8', fontSize:14 }}>{step}</div>
                </div>
              ))}
            </div>
            <div style={{ background:'#0d0d12', border:'1px solid #1a1a28', borderRadius:16, padding:28 }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>Potential earnings</div>
              {[['5 referrals on Starter','$79/mo × 5 × 20% = $79/mo'],['10 referrals on Pro','$149/mo × 10 × 20% = $298/mo'],['20 referrals mixed','~$600-800/mo passive']].map(([scenario,earn]) => (
                <div key={scenario} style={{ marginBottom:16, paddingBottom:16, borderBottom:'1px solid #1a1a28' }}>
                  <div style={{ fontSize:13, color:'#6b7280', marginBottom:4 }}>{scenario}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:'#00e87a' }}>{earn}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {tab==='settings' && <SettingsPanel supabase={supabase} user={user} API={API} />}

      </div>
    </div>
  );
}
